import React, { useState, useMemo, useEffect, useRef } from 'react';
import Select from 'react-select';
import { formOptions } from '../../constants/formOptions';
import {
  formatTasaForDisplay,
  formatNumberForDisplay,
  formatNumberForInput,
  parseAmountForState,
  parseTasaForState,
  formatComitenteForDisplay,
} from '../../utils/formatters';
import { addBusinessDays, toIsoDate } from '../../utils/dateHelpers';
import { useSaldosClientes } from '../../hooks/useSaldosClientes';
import { useNombresComerciales } from '../../hooks/useNombresComerciales';
import { createPase, updatePase } from '../../services/pasesService';
import { comercialPorDefecto } from '../../utils/rolesUsuario';
import SaldoClienteBadge from '../cauciones/SaldoClienteBadge';
import Modal from '../common/Modal';
import { fieldStyles } from '../../config/formStyles';
import { TrashIcon, PlusIcon } from '../Icons';

// Ids de activo correlativos que nunca se reciclan (act-1, act-2, ...), igual
// que las especies de licitaciones.
const nextActivoId = (activos) => {
  const maxN = activos.reduce((max, a) => {
    const n = parseInt(String(a.id).replace('act-', ''), 10);
    return Number.isFinite(n) && n > max ? n : max;
  }, 0);
  return `act-${maxN + 1}`;
};

const emptyActivo = (id) => ({ id, especie: '', cantidad: '', cantidadUsada: 0 });

// Default de vencimiento: próximo día hábil (el pase es a 24hs), editable por feriados.
const defaultFechaVencimiento = () => toIsoDate(addBusinessDays(new Date(), 1));

/**
 * Modal de alta/edición de un pase (rol Comercial). Espeja a CaucionFormModal
 * pero sin campo Días (siempre 24hs → fechaVencimiento editable), con filas
 * dinámicas de activos disponibles y el select de Comercial poblado desde el
 * padrón real (colección `comerciales`). Muestra el saldo actual del comitente
 * sin proyección post-carga (useSaldosClientes no modela pases).
 */
const PaseFormModal = ({
  isOpen,
  onClose,
  onSaved,
  clientesDb,
  especies = [],
  userRole,
  userName,
  userId,
  editingPase = null,
}) => {
  const { getSaldoComitente } = useSaldosClientes();

  const initialFormState = useMemo(() => ({
    operacion: '', nroComitente: '', cliente: '', fechaVencimiento: defaultFechaVencimiento(),
    monto: 0, tasa: 0, moneda: 'Pesos', mercado: 'BYMA',
    comercial: comercialPorDefecto({ userRole, userName }),
    comentarioComercial: '',
  }), [userRole, userName]);

  const [form, setForm] = useState(initialFormState);
  const [activos, setActivos] = useState([emptyActivo('act-1')]);
  const [montoInputValue, setMontoInputValue] = useState('');
  const [tasaInputValue, setTasaInputValue] = useState('');
  const [formError, setFormError] = useState('');
  const didInit = useRef(false);

  // Padrón de comerciales (usuarios que ingresaron con rol Comercial), sin hardcodeo.
  // Un Comercial (o un productor, que opera con ese rol) no elige comercial: el
  // select queda fijo en su nombre, así que no hace falta leer el padrón (y las
  // reglas no se lo permiten al productor).
  const comerciales = useNombresComerciales(isOpen && userRole !== 'Comercial');

  const comitenteOptions = useMemo(() =>
    clientesDb ? Object.keys(clientesDb).map(c => ({ value: c, label: formatComitenteForDisplay(c) })) : [],
    [clientesDb]
  );

  const clienteOptions = useMemo(() =>
    clientesDb ? Object.entries(clientesDb).map(([value, label]) => ({ value, label: `${label} - ${formatComitenteForDisplay(value)}` })) : [],
    [clientesDb]
  );

  const commercialOptions = useMemo(() => {
    const options = new Set(comerciales);
    if (form.comercial) options.add(form.comercial);
    if (userRole === 'Comercial' && userName) options.add(userName);
    return [...options].sort((a, b) => a.localeCompare(b, 'es'));
  }, [comerciales, form.comercial, userRole, userName]);

  // Hidrata el formulario una sola vez por apertura.
  useEffect(() => {
    if (!isOpen) { didInit.current = false; return; }
    if (didInit.current) return;
    didInit.current = true;
    setFormError('');

    if (editingPase) {
      setForm({
        operacion: editingPase.operacion || '',
        nroComitente: editingPase.nroComitente || '',
        cliente: editingPase.cliente || '',
        fechaVencimiento: editingPase.fechaVencimiento || defaultFechaVencimiento(),
        monto: editingPase.monto || 0,
        tasa: editingPase.tasa || 0,
        moneda: editingPase.moneda || 'Pesos',
        mercado: editingPase.mercado || 'BYMA',
        comercial: editingPase.comercial || '',
        comentarioComercial: editingPase.comentarioComercial || '',
      });
      const activosEdit = (editingPase.activos || []).map(a => ({
        id: a.id,
        especie: a.especie || '',
        cantidad: a.cantidad ?? '',
        cantidadUsada: a.cantidadUsada ?? 0,
      }));
      setActivos(activosEdit.length ? activosEdit : [emptyActivo('act-1')]);
      setMontoInputValue(formatNumberForDisplay(editingPase.monto));
      setTasaInputValue(formatTasaForDisplay(editingPase.tasa));
      return;
    }

    setForm(initialFormState);
    setActivos([emptyActivo('act-1')]);
    setMontoInputValue('');
    setTasaInputValue('');
  }, [isOpen, editingPase, initialFormState]);

  // Igual que en cauciones: el saldo "sin este pase" (excluyendo el que se edita)
  // y el proyectado tras aplicar el impacto del monto cargado. Un pase Tomadora
  // suma efectivo, Colocadora resta.
  const saldoModal = useMemo(() => {
    if (!isOpen || !form.nroComitente) return null;
    const opts = editingPase?.id ? { excludePaseId: editingPase.id } : {};
    const saldoSinPase = getSaldoComitente(form.nroComitente, form.moneda, opts);
    if (!saldoSinPase) return null;
    const montoInput = parseFloat(parseAmountForState(montoInputValue)) || 0;
    const impacto = form.operacion === 'Tomadora' ? montoInput : -montoInput;
    const saldoPostCarga = saldoSinPase.saldoProyectado + impacto;
    return { saldoSinPase, saldoPostCarga, moneda: form.moneda };
  }, [isOpen, form.nroComitente, form.moneda, form.operacion, editingPase, montoInputValue, getSaldoComitente]);

  const handleNroComitenteChange = (selectedOption) => {
    const nroComitente = selectedOption ? selectedOption.value : '';
    const cliente = nroComitente && clientesDb ? clientesDb[nroComitente] : '';
    setForm(prev => ({ ...prev, nroComitente, cliente }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleActivoChange = (index, field, value) => {
    setActivos(prev => prev.map((a, i) => (i === index ? { ...a, [field]: value } : a)));
  };

  const handleAddActivo = () => setActivos(prev => [...prev, emptyActivo(nextActivoId(prev))]);

  const handleRemoveActivo = (index) => {
    setActivos(prev => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.operacion) {
      setFormError('Debe seleccionar el tipo de operación (Colocadora o Tomadora).');
      return;
    }
    if (!clientesDb[form.nroComitente]) {
      setFormError('El Nro de Comitente es inválido o no existe.');
      return;
    }
    if (!form.fechaVencimiento) {
      setFormError('Debe indicar la fecha de vencimiento.');
      return;
    }
    // Middle office carga en apoyo del área comercial: el pase es de un comercial.
    if (userRole === 'MiddleOffice' && !form.comercial) {
      setFormError('Seleccioná por cuenta de qué comercial cargás el pase.');
      return;
    }
    const activosValidos = activos
      .filter(a => String(a.especie || '').trim())
      .map(a => ({ ...a, cantidad: parseFloat(parseAmountForState(String(a.cantidad))) || 0 }));
    if (activosValidos.length === 0) {
      setFormError('Cargá al menos un activo con especie.');
      return;
    }
    if (activosValidos.some(a => a.cantidad <= 0)) {
      setFormError('Cada activo debe tener una cantidad mayor a 0.');
      return;
    }
    setFormError('');

    const payload = {
      operacion: form.operacion,
      nroComitente: form.nroComitente,
      cliente: form.cliente,
      fechaVencimiento: form.fechaVencimiento,
      monto: Math.ceil(parseFloat(parseAmountForState(montoInputValue)) || 0),
      tasa: parseFloat(parseTasaForState(tasaInputValue)) || 0,
      moneda: form.moneda,
      mercado: form.mercado,
      comercial: userRole === 'Comercial' ? userName : form.comercial,
      comentarioComercial: form.comentarioComercial,
      activos: activosValidos,
    };

    try {
      if (editingPase) {
        await updatePase(editingPase.id, payload);
      } else {
        await createPase(payload, { userId, userName });
      }
      onSaved?.();
      onClose?.();
    } catch (error) {
      console.error('Error guardando el pase:', error);
      setFormError('No se pudo guardar el pase.');
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title={editingPase ? 'Editar Pase' : 'Nuevo Pase'}
      maxWidth="820px"
      onSubmit={handleSubmit}
      error={formError}
      footer={<>
        <button type="button" onClick={onClose} style={{ ...fieldStyles.button, ...fieldStyles.secondaryButton }}>Cancelar</button>
        <button type="submit" style={fieldStyles.button}>Guardar Pase</button>
      </>}
    >
      {saldoModal && saldoModal.saldoSinPase.tieneSnapshot && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem',
          marginBottom: '1.25rem', padding: '0.75rem',
          backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '0.375rem',
        }}>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6B7281', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
              Saldo actual {editingPase ? '(sin este pase)' : ''}
            </div>
            <SaldoClienteBadge
              saldoReal={saldoModal.saldoSinPase.saldoReal}
              saldoProyectado={saldoModal.saldoSinPase.saldoProyectado}
              moneda={saldoModal.moneda}
              tieneSnapshot={saldoModal.saldoSinPase.tieneSnapshot}
            />
          </div>
          <div>
            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: '#6B7281', textTransform: 'uppercase', marginBottom: '0.25rem' }}>
              Saldo proyectado post carga
            </div>
            <SaldoClienteBadge
              saldoReal={saldoModal.saldoPostCarga}
              saldoProyectado={saldoModal.saldoPostCarga}
              moneda={saldoModal.moneda}
              tieneSnapshot={saldoModal.saldoSinPase.tieneSnapshot}
            />
          </div>
        </div>
      )}

      <div className="axon-form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.85rem 1.5rem' }}>
        <div>
          <label style={fieldStyles.label}>Operación</label>
          <select name="operacion" value={form.operacion} onChange={handleInputChange} style={fieldStyles.select}>
            <option value="" disabled>Seleccionar...</option>
            {formOptions.operacion.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div>
          <label style={fieldStyles.label}>Vencimiento (24hs — próximo día hábil, editable)</label>
          <input type="date" name="fechaVencimiento" value={form.fechaVencimiento} onChange={handleInputChange} style={fieldStyles.input} required />
        </div>
        <div>
          <label style={fieldStyles.label}>Nro Comitente</label>
          <Select
            name="nroComitente"
            options={comitenteOptions}
            value={comitenteOptions.find(c => c.value === form.nroComitente) || null}
            onChange={handleNroComitenteChange}
            placeholder="Buscar por Nro de Comitente..."
            isClearable
            menuPortalTarget={document.body}
            styles={{
              control: (base) => ({ ...base, ...fieldStyles.input, padding: '0.1rem', fontSize: '0.875rem' }),
              ...fieldStyles.reactSelect,
            }}
          />
        </div>
        <div>
          <label style={fieldStyles.label}>Cliente</label>
          <Select
            name="cliente"
            options={clienteOptions}
            value={clienteOptions.find(c => c.value === form.nroComitente) || null}
            onChange={handleNroComitenteChange}
            placeholder="Buscar por nombre de cliente..."
            isClearable
            menuPortalTarget={document.body}
            styles={{
              control: (base) => ({ ...base, ...fieldStyles.input, padding: '0.1rem', fontSize: '0.875rem' }),
              ...fieldStyles.reactSelect,
            }}
          />
        </div>
        <div>
          <label style={fieldStyles.label}>Monto</label>
          <input type="text" inputMode="decimal" name="monto" value={montoInputValue}
            onChange={(e) => setMontoInputValue(e.target.value)}
            onFocus={(e) => setMontoInputValue(formatNumberForInput(e.target.value))}
            onBlur={(e) => { const rounded = Math.ceil(parseFloat(parseAmountForState(e.target.value)) || 0); setForm(p => ({ ...p, monto: rounded })); setMontoInputValue(formatNumberForDisplay(String(rounded))); }}
            style={fieldStyles.input} required />
        </div>
        <div>
          <label style={fieldStyles.label}>Tasa (%) - Opcional</label>
          <input type="text" inputMode="decimal" name="tasa" value={tasaInputValue}
            onChange={(e) => setTasaInputValue(e.target.value)}
            onFocus={(e) => setTasaInputValue(formatNumberForInput(e.target.value))}
            onBlur={(e) => { const parsed = parseTasaForState(e.target.value); setForm(p => ({ ...p, tasa: parseFloat(parsed) || 0 })); setTasaInputValue(formatTasaForDisplay(parsed)); }}
            style={fieldStyles.input} placeholder="Dejar vacío si el Trader debe definir la tasa" />
        </div>
        <div>
          <label style={fieldStyles.label}>Moneda</label>
          <select name="moneda" value={form.moneda} onChange={handleInputChange} style={fieldStyles.select}>
            {formOptions.moneda.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div>
          <label style={fieldStyles.label}>Mercado</label>
          <select name="mercado" value={form.mercado} onChange={handleInputChange} style={fieldStyles.select}>
            {formOptions.mercado.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div>
          <label style={fieldStyles.label}>Comercial</label>
          <select name="comercial" value={form.comercial} onChange={handleInputChange} style={fieldStyles.select} disabled={userRole === 'Comercial'}>
            {userRole !== 'Comercial' && <option value="">Seleccione un Comercial</option>}
            {commercialOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        <div style={{ gridColumn: 'span 2' }}>
          <label style={fieldStyles.label}>Comentario Comercial</label>
          <textarea name="comentarioComercial" value={form.comentarioComercial} onChange={handleInputChange}
            style={{ ...fieldStyles.textarea, minHeight: '70px', backgroundColor: userRole === 'Trader' ? '#F3F4F6' : '#FFFFFF' }}
            disabled={userRole === 'Trader'}
            placeholder={userRole === 'Comercial' ? 'Ingrese un comentario...' : 'Campo deshabilitado para Traders'} />
        </div>
      </div>

      <div style={{ ...fieldStyles.sectionHeader, marginTop: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Activos disponibles para el pase</span>
        <button type="button" onClick={handleAddActivo} style={{ ...fieldStyles.button, padding: '0.3rem 0.75rem', fontSize: '0.75rem' }}>
          <PlusIcon /> Agregar activo
        </button>
      </div>
      <p style={{ fontSize: '0.75rem', color: '#6B7281', margin: '0.5rem 0 0' }}>
        El comercial carga la especie y la cantidad disponible de cada una. El trader elige cuánto usar de cada activo al operar.
      </p>

      {activos.map((activo, index) => (
        <div key={activo.id} style={{
          display: 'grid', gridTemplateColumns: '2fr 1fr auto', gap: '0.75rem 1rem',
          padding: '0.85rem', marginTop: '0.75rem', backgroundColor: '#F9FAFB',
          border: '1px solid #E5E7EB', borderRadius: '6px', alignItems: 'end',
        }}>
          <div>
            <label style={fieldStyles.label}>Especie</label>
            <input
              list="pase-especies-list"
              value={activo.especie}
              onChange={(e) => handleActivoChange(index, 'especie', e.target.value.toUpperCase())}
              style={fieldStyles.input}
              autoComplete="off"
              placeholder="Ej: GD30"
            />
          </div>
          <div>
            <label style={fieldStyles.label}>Cantidad disponible (VN)</label>
            <input
              type="text"
              inputMode="decimal"
              value={activo.cantidad}
              onChange={(e) => handleActivoChange(index, 'cantidad', e.target.value)}
              style={fieldStyles.input}
              placeholder="Ej: 100000"
            />
          </div>
          <div style={{ alignSelf: 'center' }}>
            {activos.length > 1 && (
              <button
                type="button"
                onClick={() => handleRemoveActivo(index)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', padding: '0.25rem' }}
                title="Quitar activo"
              >
                <TrashIcon />
              </button>
            )}
          </div>
        </div>
      ))}
      <datalist id="pase-especies-list">
        {Array.isArray(especies) && especies.map(esp => <option key={esp.id} value={esp.id} />)}
      </datalist>
    </Modal>
  );
};

export default PaseFormModal;
